import React, { useMemo, useState, useCallback, useEffect } from 'react';

const headerCols = [
  { key: 'sessionId', label: 'Session' },
  { key: 'playerId', label: 'Player' },
  { key: 'device', label: 'Device' },
  { key: 'latencyMs', label: 'Latency' },
  { key: 'status', label: 'Status' },
  { key: 'updatedAt', label: 'Updated' },
  { key: 'age', label: 'HB Age' },
];

const statusBadge = (status) => {
  const s = status || 'offline';
  const color =
    s === 'online' ? 'success' : s === 'idle' ? 'warning' : 'secondary';
  return (
    <span className={`badge bg-${color}`} style={{ minWidth: 64 }}>
      {s}
    </span>
  );
};

const fmtAge = (ms) => {
  if (ms == null) return '—';
  const sec = Math.max(0, Math.floor(ms / 1000));
  return `${sec}s`;
};

export default function SessionTable({ rows = [] }) {
  const [sortKey, setSortKey] = useState('updatedAt');
  const [sortDir, setSortDir] = useState('desc');
  const [isCompact, setIsCompact] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const onResize = () => setIsCompact(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onSort = useCallback(
    (key) => {
      if (sortKey === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDir('asc');
      }
    },
    [sortKey],
  );

  const sorted = useMemo(() => {
    const cmp = (a, b) => {
      const av = a?.[sortKey];
      const bv = b?.[sortKey];
      if (av === bv) return 0;
      if (sortDir === 'asc') return av > bv ? 1 : -1;
      return av < bv ? 1 : -1;
    };
    return [...rows].map((r) => {
      const updatedAt = r.updatedAt || r.timestamp || r.lastSeen;
      const age = updatedAt ? Date.now() - updatedAt : null;
      return { ...r, updatedAt, age };
    }).sort(cmp);
  }, [rows, sortDir, sortKey]);

  const toggle = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="card shadow-sm w-100">
      <div className="card-body" style={{ minWidth: 0, overflowX: 'auto' }}>
        <h5 className="card-title mb-3">Sessions</h5>
        {!isCompact && (
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle">
              <thead>
                <tr>
                  {headerCols.map((c) => (
                    <th
                      key={c.key}
                      style={{ cursor: 'pointer' }}
                      onClick={() => onSort(c.key)}
                    >
                      {c.label}{' '}
                      {sortKey === c.key ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((s) => (
                  <tr key={s.sessionId}>
                    <td style={{ fontFamily: 'monospace' }}>{s.sessionId}</td>
                    <td style={{ fontFamily: 'monospace' }}>{s.playerId || '—'}</td>
                    <td>{s.device || s.deviceId || '—'}</td>
                    <td>{s.latencyMs != null ? `${s.latencyMs} ms` : '—'}</td>
                    <td>{statusBadge(s.status)}</td>
                    <td>
                      {s.updatedAt
                        ? new Date(s.updatedAt).toLocaleTimeString()
                        : '—'}
                    </td>
                    <td>{fmtAge(s.age)}</td>
                  </tr>
                ))}
                {!sorted.length && (
                  <tr>
                    <td colSpan={headerCols.length} className="text-center text-muted">
                      No sessions
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {isCompact && (
          <div className="d-flex flex-column gap-2">
            {sorted.length ? (
              sorted.map((s) => {
                const open = !!expanded[s.sessionId];
                return (
                  <div
                    key={s.sessionId}
                    className="border rounded p-2 bg-light"
                    style={{ fontSize: '0.95rem' }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <div style={{ fontFamily: 'monospace' }}>
                          {s.sessionId}
                        </div>
                        <div className="small text-muted">
                          Player: {s.playerId || '—'}
                        </div>
                        <div className="small text-muted">
                          Latency:{' '}
                          {s.latencyMs != null ? `${s.latencyMs} ms` : '—'}
                        </div>
                      </div>
                      <div className="text-end">
                        {statusBadge(s.status)}
                        <button
                          type="button"
                          className="btn btn-link btn-sm d-block p-0 mt-1"
                          onClick={() => toggle(s.sessionId)}
                          aria-expanded={open}
                        >
                          {open ? 'Hide details' : 'Show details'}
                        </button>
                      </div>
                    </div>
                    {open && (
                      <div className="mt-2 small">
                        <div>Device: {s.device || s.deviceId || '—'}</div>
                        <div>
                          Updated:{' '}
                          {s.updatedAt
                            ? new Date(s.updatedAt).toLocaleTimeString()
                            : '—'}
                        </div>
                        <div>HB Age: {fmtAge(s.age)}</div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center text-muted">No sessions</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
